/**
 * Server state for build step 8 — documents and the Approvals board (the same Document rows). One hook per route in
 * packages/contracts routes.step8 `documents.*`:
 *   GET /documents (Documents tab, Approvals board + its stats), GET /documents/{id} (the drawer, with versions),
 *   POST /documents/sign-upload → PUT the bytes → POST /documents ("Upload a file" with file_key, or
 *   "New item for approval" without), PATCH /documents/{id}, POST /documents/{id}/versions,
 *   POST /documents/{id}/stage ("Move to"), GET /documents/{id}/versions/{vid}/file ("Open").
 * The server scopes every read (managers all clients, a member their SPOC clients); nothing is filtered here.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { CreateDocumentBody, DocStage, DocType, DocumentListItem, ListDocumentsQuery, UpdateDocumentBody } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../api';
import { allPages, refreshAfterWrite } from './work';
import { openBlankTab } from './tab';

/** Prototype TYPEC: the type chip's colour. */
export const TYPE_CHIP: Record<DocType, string> = { MIS: 'info', Deck: 'brand', Report: 'hot', Plan: '', RCA: 'warm' };

/** Prototype STAGES order, with the column dot of VIEWS.approvals. */
export const STAGE_ORDER: readonly DocStage[] = ['drafting', 'internal_review', 'with_client', 'changes_requested', 'approved'];
export const STAGE_DOT: Record<DocStage, { cls: string; style?: { background: string } }> = {
  drafting: { cls: 'mute' },
  internal_review: { cls: 'mute' },
  with_client: { cls: 'cold', style: { background: 'var(--brand)' } },
  changes_requested: { cls: 'warm' },
  approved: { cls: 'hot' },
};

export const dkeys = {
  all: ['documents'] as const,
  list: (q: Omit<ListDocumentsQuery, 'cursor' | 'limit'>) => ['documents', 'list', q] as const,
  one: (id: string) => ['documents', 'one', id] as const,
};

/** A document write changes the tab, the board, the drawer and the menu badges. */
export function refreshDocuments(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: dkeys.all });
  refreshAfterWrite(qc);
}

/**
 * An If-Match write that lost a race (412: someone else changed it; 409: its stage moved on) refreshes too, so the
 * drawer and board show the current stage and version before the person tries again (as useWorkWrite).
 */
const refreshOnConflict = (qc: QueryClient) => (e: Error) => {
  if (e instanceof ApiFailure && (e.status === 412 || e.status === 409)) refreshDocuments(qc);
};

// ---------------------------------------------------------------- reads

/** Every page of GET /documents for these filters, with the stats of the first page (they cover the whole set). */
export function useDocuments(q: Omit<ListDocumentsQuery, 'cursor' | 'limit'>) {
  return useQuery({
    queryKey: dkeys.list(q),
    queryFn: async () => {
      let stats: Awaited<ReturnType<typeof listPage>>['stats'] | null = null;
      const rows = await allPages<DocumentListItem>(async (cursor) => {
        const p = await listPage(q, cursor);
        stats ??= p.stats;
        return p;
      });
      return { rows, stats: stats! };
    },
  });
}
const listPage = (q: Omit<ListDocumentsQuery, 'cursor' | 'limit'>, cursor: string | undefined) =>
  call('documents.list', { query: { ...q, limit: 200, ...(cursor ? { cursor } : {}) } });

export function useDocument(id: string | null) {
  return useQuery({ queryKey: dkeys.one(id ?? ''), queryFn: () => call('documents.get', { params: { id: id! } }), enabled: !!id });
}

// ---------------------------------------------------------------- writes

/** Sign a slot and PUT the file straight to storage; returns the key to send as `file_key`. */
export async function uploadDocumentFile(f: File): Promise<string> {
  const content_type = f.type || 'application/octet-stream';
  const slot = await call('documents.signUpload', { body: { filename: f.name, bytes: f.size, content_type }, idempotencyKey: newIdempotencyKey() });
  const failed = new Error('The file did not upload. Check your connection and try again.');
  const headers = new Headers(slot.headers);
  if (!headers.has('content-type')) headers.set('content-type', content_type);
  let res: Response;
  try {
    res = await fetch(slot.url, { method: 'PUT', headers, body: f });
  } catch {
    throw failed;
  }
  if (!res.ok) throw failed;
  return slot.key;
}

/** The contract's document Name: 2–160 characters after trimming. */
export const DOC_NAME_MIN = 2;
export const DOC_NAME_MAX = 160;

/**
 * The prototype names an uploaded document after its file, without the extension. A stem under the Name minimum
 * ("a.pdf") keeps the extension, and a still-short one is padded, so the create never fails with a 422 after the
 * bytes are already uploaded; a long one is cut to the Name maximum.
 */
export const nameFromFile = (filename: string) => {
  const full = filename.trim();
  const stem = full.replace(/\.[^.]+$/, '').trim();
  const base = stem.length >= DOC_NAME_MIN ? stem : full.length >= DOC_NAME_MIN ? full : `${full || 'Untitled'} file`;
  return base.slice(0, DOC_NAME_MAX).trim();
};

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: CreateDocumentBody; file?: File }) =>
      (async () => {
        const file_key = v.file ? await uploadDocumentFile(v.file) : undefined;
        return call('documents.create', { body: { ...v.body, ...(file_key ? { file_key } : {}) }, idempotencyKey: newIdempotencyKey() });
      })(),
    onSuccess: () => refreshDocuments(qc),
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; version: number; body: UpdateDocumentBody }) => call('documents.update', { params: { id: v.id }, body: v.body, ifMatch: v.version }),
    onSuccess: () => refreshDocuments(qc),
    onError: refreshOnConflict(qc),
  });
}

export function useAddVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; file: File; note?: string }) => {
      const file_key = await uploadDocumentFile(v.file);
      return call('documents.addVersion', { params: { id: v.id }, body: { file_key, ...(v.note ? { note: v.note } : {}) }, idempotencyKey: newIdempotencyKey() });
    },
    onSuccess: () => refreshDocuments(qc),
  });
}

export function useMoveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; version: number; stage: DocStage }) => call('documents.move', { params: { id: v.id }, body: { stage: v.stage }, ifMatch: v.version }),
    onSuccess: () => refreshDocuments(qc),
    onError: refreshOnConflict(qc),
  });
}

/**
 * "Open": a short-lived link to one version's file. The tab opens synchronously (so no popup blocker stops it) and
 * is pointed at the link once the server answers; without a version id it takes the newest version with a file.
 */
export async function openDocumentFile(id: string, vid?: string): Promise<void> {
  const w = openBlankTab();
  try {
    let v = vid;
    if (!v) {
      const d = await call('documents.get', { params: { id } });
      v = d.versions.find((x) => x.has_file)?.id;
      if (!v) throw new Error('This document has no file yet.');
    }
    const link = await call('documents.fileUrl', { params: { id, vid: v } });
    if (w) w.location.href = link.url;
    else window.location.assign(link.url);
  } catch (e) {
    w?.close();
    throw e;
  }
}
